import React from 'react';
import { Link } from 'react-router-dom';
import { LuArrowUpRight, LuBookOpen } from 'react-icons/lu';
import NotebookAvatar from './NotebookAvatar';
import { decodeHtmlEntities } from '../../utils/formatStructuredAnswer';

function visibilityLabel(visibility) {
  const v = (visibility || 'PRIVATE').toUpperCase();
  if (v === 'PUBLIC') return 'Public';
  if (v === 'TEAM') return 'Team';
  if (v === 'UNLISTED') return 'Shared';
  return 'Private';
}

function formatQuestionCount(n) {
  const count = Number(n) || 0;
  return count === 1 ? '1 question' : `${count} questions`;
}

export default function LearnNotebookCard({ notebook, layout = 'tile', as = 'link' }) {
  const isTile = layout === 'tile';
  const isBook = layout === 'book';
  const progress = typeof notebook.progressPercent === 'number' ? notebook.progressPercent : null;
  const title = decodeHtmlEntities(notebook.title || 'Untitled Q&A');
  const desc = decodeHtmlEntities(notebook.description || '').trim();
  const vis = (notebook.visibility || 'private').toLowerCase();

  let inner;
  if (isBook) {
    inner = (
      <div className="learn-nb-book-wrap">
        <div className="learn-nb-book-volume">
          <div className="learn-nb-book-spine">
            <span className="learn-nb-book-spine-text">{title}</span>
          </div>
          <div className="learn-nb-book-front">
            <div className="learn-nb-book-cover-frame">
              <div className="learn-nb-book-cover-top">
                <span className={`learn-nb-pill learn-nb-pill--${vis}`}>{visibilityLabel(notebook.visibility)}</span>
              </div>
              <h3 className="learn-nb-book-cover-title">{title}</h3>
              <div className="learn-nb-book-cover-mid">
                <div className="learn-nb-book-cover-band" aria-hidden />
                <div className="learn-nb-book-cover-emblem" aria-hidden>
                  <LuBookOpen className="learn-nb-book-cover-icon" />
                </div>
              </div>
              <p className="learn-nb-book-cover-publisher">
                <span className="learn-nb-book-cover-publisher-kicker">Publisher</span>
                QnaRead
              </p>
              <p className="learn-nb-book-cover-meta">{formatQuestionCount(notebook.questionCount)}</p>
            </div>
          </div>
          <div className="learn-nb-book-pages-edge" />
        </div>
        <div className="learn-nb-book-slip">
          {desc ? <p className="learn-nb-book-desc">{desc}</p> : (
            <p className="learn-nb-book-desc learn-nb-book-desc--muted">No description yet.</p>
          )}
          <dl className="learn-nb-book-stats">
            <div>
              <dt>Questions</dt>
              <dd>{formatQuestionCount(notebook.questionCount)}</dd>
            </div>
            <div>
              <dt>Module</dt>
              <dd className={notebook.moduleLabel ? '' : 'learn-nb-book-stat-empty'}>
                {notebook.moduleLabel || '—'}
              </dd>
            </div>
            <div>
              <dt>Progress</dt>
              <dd className={progress !== null ? '' : 'learn-nb-book-stat-empty'}>
                {progress !== null ? `${progress}% read` : '—'}
              </dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>{visibilityLabel(notebook.visibility)}</dd>
            </div>
          </dl>
          <div
            className={`learn-nb-book-progress${progress === null ? ' learn-nb-book-progress--empty' : ''}`}
            aria-hidden={progress === null}
          >
            <div className="home-daily-track learn-nb-card-track">
              {progress !== null ? (
                <div className="home-daily-fill" style={{ width: `${progress}%` }} />
              ) : null}
            </div>
          </div>
          <span className="learn-nb-book-foot">
            Open Q&amp;A
            <LuArrowUpRight aria-hidden />
          </span>
        </div>
      </div>
    );
  } else if (isTile) {
    inner = (
      <>
        <div className="learn-nb-card-top">
          <NotebookAvatar size="md" />
          <span className={`learn-nb-pill learn-nb-pill--${vis}`}>
            {visibilityLabel(notebook.visibility)}
          </span>
        </div>
        <div className="learn-nb-card-body">
          <h3 className="learn-nb-card-title">{title}</h3>
          {desc ? <p className="learn-nb-card-desc">{desc}</p> : null}
          <p className="learn-nb-card-meta">
            <span>{formatQuestionCount(notebook.questionCount)}</span>
            {notebook.moduleLabel ? <span className="learn-nb-card-module">{notebook.moduleLabel}</span> : null}
          </p>
          {progress !== null && (
            <div className="learn-nb-card-progress">
              <div className="learn-nb-card-progress-head">
                <span>Reading progress</span>
                <span>{progress}%</span>
              </div>
              <div className="home-daily-track learn-nb-card-track" aria-hidden>
                <div className="home-daily-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>
        <span className="learn-nb-card-foot">
          Open Q&amp;A
          <LuArrowUpRight aria-hidden />
        </span>
      </>
    );
  } else {
    inner = (
      <>
        <NotebookAvatar size="md" />
        <div className="learn-nb-card-body">
          <h3 className="learn-nb-card-title">{title}</h3>
          <p className="learn-nb-card-meta">
            {formatQuestionCount(notebook.questionCount)}
            {progress !== null ? ` · ${progress}% read` : ''}
          </p>
          {progress !== null && (
            <div className="home-daily-track learn-nb-card-track" aria-hidden>
              <div className="home-daily-fill" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </>
    );
  }

  const className = [
    'learn-nb-card',
    isBook ? 'learn-nb-card--book' : isTile ? 'learn-nb-card--tile' : 'learn-nb-card--row',
    as === 'button' ? 'learn-nb-card--cta' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (as === 'button') {
    return (
      <button type="button" className={className}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={`/hub/notebooks/${notebook.id}`} className={className}>
      {inner}
    </Link>
  );
}
